/**
 * «Позвонить» из шапки диалога, секретного чата и профиля (изменение v1.14, C-42).
 *
 * Звонок живёт в личном диалоге пары: из секретного чата и профиля он находится в реплике или
 * открывается `POST /conversations`. Если настройка `calls_p2p` требует ретранслятор, а сервер
 * без TURN, — явный вопрос: прямое соединение покажет собеседнику IP-адрес.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react'

import { callManager } from '@features/calls/manager'
import { relayRequired } from '@features/calls/relay'
import { api, unwrap } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import { contactsRepo, conversationsRepo } from '@shared/db'
import { unlockTones } from '@shared/rtc/tones'
import { Button, Dialog, useToast } from '@shared/ui'

import { useAppStore } from '../../model/app-store'

async function directConversationId(peerId: string): Promise<string> {
  const known = await conversationsRepo.byCounterpart(peerId)
  if (known && known.kind === 'direct') return known.id
  const created = unwrap(await api.POST('/conversations', { body: { user_id: peerId } as never }))
  await conversationsRepo.put(created)
  return created.id
}

export function useStartCall(): { start: (peerId: string) => void; busy: boolean; dialog: ReactNode } {
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState(false)
  const answer = useRef<((agreed: boolean) => void) | null>(null)

  const askDirect = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        answer.current = resolve
        setAsking(true)
      }),
    [],
  )

  const reply = useCallback((agreed: boolean) => {
    setAsking(false)
    answer.current?.(agreed)
    answer.current = null
  }, [])

  const start = useCallback(
    (peerId: string) => {
      // Разблокировка звука — синхронно в жесте: Safari не разрешит её после await.
      unlockTones()
      if (busy) return
      void (async () => {
        const { config, me } = useAppStore.getState()
        if (!config?.features?.['calls']) {
          toast.show('Звонки сейчас недоступны')
          return
        }
        setBusy(true)
        try {
          if (!(await callManager.ensureSupported())) return
          let conversationId: string
          try {
            conversationId = await directConversationId(peerId)
          } catch (error) {
            toast.show(
              error instanceof ApiError && error.code === 'user_unavailable'
                ? 'Пользователь недоступен для звонка'
                : 'Не удалось начать звонок',
              { tone: 'danger' },
            )
            return
          }
          let relayOnly = relayRequired(me?.settings.calls_p2p, await contactsRepo.has(peerId))
          if (relayOnly && !config.features?.['calls_relay']) {
            if (!(await askDirect())) return
            relayOnly = false
          }
          await callManager.startOutgoing({ conversationId, peerId, relayOnly })
        } catch {
          // Сбой до старта сессии — звонка нет, объяснение коротким тостом.
          toast.show('Не удалось начать звонок', { tone: 'danger' })
        } finally {
          setBusy(false)
        }
      })()
    },
    [askDirect, busy, toast],
  )

  const dialog = (
    <Dialog
      open={asking}
      onClose={() => reply(false)}
      title="Позвонить напрямую?"
      description="Сервер-посредник для звонков сейчас недоступен. При прямом соединении собеседник увидит ваш IP-адрес. Разговор всё равно зашифрован сквозным ключом."
      footer={
        <>
          <Button variant="secondary" onClick={() => reply(false)}>
            Отмена
          </Button>
          <Button onClick={() => reply(true)}>Позвонить напрямую</Button>
        </>
      }
    />
  )

  return { start, busy, dialog }
}
